import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const ClientContext = createContext();

export const useClient = () => useContext(ClientContext);

export const ClientProvider = ({ children }) => {
  const { token, user } = useAuth();
  const [clients, setClients] = useState([]);
  const [activeClient, setActiveClient] = useState(null);
  const [loadingClients, setLoadingClients] = useState(true);

  const fetchClients = async () => {
    if (!token) return;
    try {
      const res = await axios.get('/api/clients', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setClients(res.data.clients);
        const storedId = localStorage.getItem('activeClientId');
        if (storedId) {
            const found = res.data.clients.find(c => c._id === storedId);
            if (found) setActiveClient(found);
        }
      }
    } catch (error) {
      console.error('Failed to fetch clients', error);
    } finally {
      setLoadingClients(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchClients();
    } else {
      setClients([]);
      setActiveClient(null);
      localStorage.removeItem('activeClientId');
      setLoadingClients(false);
    }
  }, [token]);

  const handleSetActiveClient = (client) => {
    setActiveClient(client);
    if (client) {
        localStorage.setItem('activeClientId', client._id);
    } else {
        localStorage.removeItem('activeClientId');
    }
  };

  const addClient = async (clientData) => {
    try {
      const res = await axios.post('/api/clients/add', clientData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setClients(prev => [res.data.client, ...prev]);
        return res.data;
      }
    } catch (error) {
      console.error('Failed to add client', error);
      throw error;
    }
  };

  const updateClient = async (id, clientData) => {
    try {
      const res = await axios.put(`/api/clients/${id}`, clientData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setClients(prev => prev.map(c => c._id === id ? res.data.client : c));
        if (activeClient && activeClient._id === id) {
          handleSetActiveClient(res.data.client);
        }
        return res.data;
      }
    } catch (error) {
      console.error('Failed to update client', error);
      throw error;
    }
  };

  const clearActiveClient = () => {
    handleSetActiveClient(null);
  };

  return (
    <ClientContext.Provider value={{
      clients,
      activeClient,
      setActiveClient: handleSetActiveClient,
      clearActiveClient,
      addClient,
      updateClient,
      fetchClients,
      loadingClients
    }}>
      {children}
    </ClientContext.Provider>
  );
};
